import React, { ReactNode, useCallback } from "react";

import { SoccerFormationsProps, basketballSets, hockeyFormations, soccerFormations } from "../../../config";
import { basketballSetNameToThumbnailMap, soccerSetNameToThumbnailMap } from "../../../images";
import { usePlayContext } from "../../../pages";
import { Sport } from "../../../redux/slices";
import { CreateNewPlayerProps, createNewPlayer } from "../../../utils";

export interface PositionSet {
  label: string;
  setThumbnail?: ReactNode;
  positions: CreateNewPlayerProps[];
  onClick?: () => void;
}

interface InvertFormationProps extends Pick<PositionSet, "positions"> {
  fullWidth: number;
}
const invertFormation = ({ positions, fullWidth }: InvertFormationProps) =>
  positions.map((position) => ({ ...position, pos: { ...position.pos, x: fullWidth - position.pos.x } }));

export interface PositionSetConfig extends Pick<PositionSet, "label" | "positions"> {
  fieldSize: "FULL" | "HALF";
}

interface PositionSetConfigWithThumbnail extends PositionSetConfig {
  setThumbnailUrl?: string;
}

export const usePositionSets = (): PositionSet[] => {
  const { frameStencilsApi, playStateApi } = usePlayContext();
  const { addStencils } = frameStencilsApi;
  const { playState } = playStateApi;
  const { sport, playSurfaceId, playSurfaceView } = playState;

  const addPlayers = useCallback(
    (set: CreateNewPlayerProps[]) => () => {
      addStencils({ stencils: set.map((player) => createNewPlayer(player)) });
    },
    [addStencils]
  );

  const positionSets: PositionSetConfigWithThumbnail[] = (() => {
    switch (sport) {
      case Sport.BASKETBALL:
        return basketballSets.map((set) => ({
          ...set,
          setThumbnailUrl: basketballSetNameToThumbnailMap.get(set.label),
        }));
      case Sport.HOCKEY:
        return hockeyFormations;
      case Sport.SOCCER: {
        const pitchDimension: SoccerFormationsProps["pitchDimension"] = (() => {
          switch (playSurfaceView) {
            case "FULL":
              return "FULL";
            case "LEFT_HALF":
            case "RIGHT_HALF":
              return "HALF";
            case "LEFT_QUARTER":
            case "RIGHT_QUARTER":
              return "GOAL";
            default:
              return "FULL";
          }
        })();
        const size: SoccerFormationsProps["size"] = playSurfaceId === "Small Pitch" ? "SMALL" : "STANDARD";
        const inverted = playSurfaceView === "RIGHT_HALF" || playSurfaceView === "RIGHT_QUARTER";
        return soccerFormations({ size, pitchDimension }).map((formation) => ({
          ...formation,
          positions: inverted
            ? invertFormation({ positions: formation.positions, fullWidth: size === "STANDARD" ? 120 : 100 })
            : formation.positions,
          setThumbnailUrl: soccerSetNameToThumbnailMap.get(formation.label),
        }));
      }
    }
  })();

  return positionSets.map(({ label, positions, setThumbnailUrl }) => ({
    label,
    positions,
    setThumbnail: setThumbnailUrl ? <img src={setThumbnailUrl} /> : undefined,
    onClick: addPlayers(positions),
  }));
};
